import type { TextEntity } from "@/api/business/types/printing"
import type { OrderDetailVO } from "@/api/user/types/member"
import { buildPrepaymentSettlementDisplayRows } from "@/features/settlement/prepaymentSettlementBreakdown"

export type ReceiptPaymentLike = {
  changeAmount?: number | null
  payAmount?: number | null
  paymentDescription?: string | null
  payStatus?: number | null
  payType?: number | null
  payTypeName?: string | null
}

export type BuildReceiptPaymentInfoOptions = {
  fallbackChangeAmount?: number
  fallbackPayments?: ReceiptPaymentLike[]
  mergePaymentRows?: boolean
  prebuiltPaymentRows?: TextEntity[]
}

const PAYMENT_TYPE_NAMES: Record<number, string> = {
  1: "现金",
  2: "会员支付",
  3: "微信",
  4: "支付宝",
  5: "会员挂账",
  7: "其他",
}

export function buildReceiptPaymentInfo(
  data: OrderDetailVO,
  options: BuildReceiptPaymentInfoOptions = {},
): TextEntity[] {
  if (data.orderInfo?.orderStatus === 5) {
    return []
  }

  const persistedRecords = (data.payRecordList ?? []) as ReceiptPaymentLike[]
  const prepaymentSettlementRecords = buildPrepaymentSettlementDisplayRows(
    data.prepaymentSettlementBreakdown,
  ).map(row => ({
    payAmount: row.amount,
    payStatus: 1,
    payType: row.payType,
    payTypeName: row.label,
  }))
  const usePersistedRecords = persistedRecords.length > 0 || prepaymentSettlementRecords.length > 0
  const payments = usePersistedRecords
    ? [...persistedRecords.filter(isSuccessfulPayment), ...prepaymentSettlementRecords]
    : (options.fallbackPayments ?? [])
  const rows = options.prebuiltPaymentRows
    ? normalizePrebuiltPaymentRows(options.prebuiltPaymentRows)
    : buildPaymentRows(
        payments,
        usePersistedRecords ? 0 : normalizeNonNegativeCents(options.fallbackChangeAmount),
        options.mergePaymentRows ?? false,
      )

  const summary = data.benefitSummary
  const usedSingleShopMemberPayment = payments.some(payment =>
    payment.payType === 2 && normalizeCents(payment.payAmount) > 0,
  )
  const usedBrandStoredValue = normalizeNonNegativeCents(summary?.storedValueDeducted) > 0
  const usedBrandCashback = normalizeNonNegativeCents(summary?.cashbackDeducted) > 0

  if (usedSingleShopMemberPayment || usedBrandStoredValue) {
    appendBalanceRow(
      rows,
      "会员余额",
      usedBrandStoredValue ? summary?.storedValueBalanceAfter : data.memberInfo?.balance,
    )
  }
  if (usedBrandCashback) {
    appendBalanceRow(rows, "返现账户余额", summary?.cashbackBalanceAfter)
  }

  return rows
}

function buildPaymentRows(
  payments: ReceiptPaymentLike[],
  fallbackChangeAmount: number,
  mergePaymentRows: boolean,
) {
  const mergedRows = new Map<string, number>()
  const unmergedRows: Array<{ amount: number, text: string }> = []
  let standaloneCashChange = 0
  let remainingFallbackChange = fallbackChangeAmount

  payments.forEach((payment) => {
    let payAmount = normalizeCents(payment.payAmount)
    const changeAmount = normalizeCents(payment.changeAmount)
    if (isStandaloneCashChange(payment, payAmount, changeAmount)) {
      standaloneCashChange += changeAmount
      return
    }
    if (payment.payType === 1 && payAmount > 0 && remainingFallbackChange > 0) {
      const deductedChange = Math.min(payAmount, remainingFallbackChange)
      payAmount -= deductedChange
      remainingFallbackChange -= deductedChange
    }
    if (payAmount === 0) {
      return
    }
    const paymentName = resolvePaymentName(payment)
    if (mergePaymentRows) {
      mergedRows.set(paymentName, (mergedRows.get(paymentName) ?? 0) + payAmount)
    } else {
      unmergedRows.push({ amount: payAmount, text: paymentName })
    }
  })

  const paymentRows = mergePaymentRows
    ? Array.from(mergedRows, ([text, amount]) => ({ amount, text }))
    : unmergedRows
  const rows = paymentRows.map(({ amount, text }) => ({
    text,
    value: formatCents(amount),
  }))
  if (standaloneCashChange < 0) {
    rows.push({ text: "现金找零", value: formatCents(-standaloneCashChange) })
  }
  return rows
}

function normalizePrebuiltPaymentRows(rows: TextEntity[]) {
  return rows.flatMap((row) => {
    const value = Number(row.value)
    if (Number.isFinite(value) && value === 0) {
      return []
    }
    return [{ ...row }]
  })
}

function isSuccessfulPayment(payment: ReceiptPaymentLike) {
  return payment.payStatus == null || payment.payStatus === 1
}

function isStandaloneCashChange(payment: ReceiptPaymentLike, payAmount: number, changeAmount: number) {
  return payment.payType === 1 && payAmount < 0 && payAmount === changeAmount
}

function resolvePaymentName(payment: ReceiptPaymentLike) {
  const explicitName = String(payment.payTypeName || payment.paymentDescription || "").trim()
  return explicitName || PAYMENT_TYPE_NAMES[Number(payment.payType)] || "其他"
}

function appendBalanceRow(rows: TextEntity[], text: string, balance: number | null | undefined) {
  if (balance == null || !Number.isFinite(Number(balance))) {
    return
  }
  rows.push({ text, value: formatCents(normalizeCents(balance)) })
}

function normalizeNonNegativeCents(value: number | null | undefined) {
  return Math.max(normalizeCents(value), 0)
}

function normalizeCents(value: number | null | undefined) {
  const amount = Number(value ?? 0)
  return Number.isFinite(amount) ? Math.round(amount) : 0
}

function formatCents(value: number) {
  return (value / 100).toFixed(2)
}
